import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ArrowRight,
  Bell,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Eye,
  EyeOff,
  Gauge,
  Mail,
  MapPin,
  ShieldCheck,
  Sparkles,
  Users,
  Wifi,
} from 'lucide-react';
import { Navigate } from '../navigation';
import { useApp } from '../AppContext';
import { BrandLogo, BrandedLoading, Button, ErrorState } from '../components';
import { isMobileAppRuntime } from '../mobile-runtime';
import { ApiError } from '../api';
import '../login.css';

const mobileLoginSlides = [
  {
    id: 'day',
    eyebrow: 'Your workday, simplified',
    title: 'Everything important, already in view.',
    description: 'Tasks, calendar and people stay together in one calm workspace.',
  },
  {
    id: 'attendance',
    eyebrow: 'Trusted attendance',
    title: 'Check in with confidence.',
    description: 'Face, office and network proof keep every attendance record reliable.',
  },
  {
    id: 'connected',
    eyebrow: 'Always connected',
    title: 'Never miss what matters.',
    description: 'Messages, approvals and reminders reach you wherever the workday goes.',
  },
] as const;

type MobileLoginSlideId = (typeof mobileLoginSlides)[number]['id'];

function MobileLoginArtwork({ slide }: { slide: MobileLoginSlideId }) {
  if (slide === 'attendance') return <div className="login-art login-art--attendance" aria-hidden="true">
    <span className="login-art__orbit login-art__orbit--one" />
    <span className="login-art__orbit login-art__orbit--two" />
    <div className="login-art__attendance-card">
      <span className="login-art__pin"><MapPin size={28} /></span>
      <strong>Office verified</strong>
      <small>Ready to check in</small>
      <i><CheckCircle2 size={14} /></i>
    </div>
    <span className="login-art__float login-art__float--wifi"><Wifi size={16} /></span>
    <span className="login-art__float login-art__float--shield"><ShieldCheck size={17} /></span>
  </div>;

  if (slide === 'connected') return <div className="login-art login-art--connected" aria-hidden="true">
    <span className="login-art__glow" />
    <div className="login-art__notification login-art__notification--mail">
      <span><Mail size={16} /></span><div><strong>New message</strong><small>Project update received</small></div>
    </div>
    <div className="login-art__notification login-art__notification--task">
      <span><CheckCircle2 size={16} /></span><div><strong>Task approved</strong><small>Ready for delivery</small></div>
    </div>
    <span className="login-art__bell"><Bell size={22} /></span>
  </div>;

  return <div className="login-art login-art--day" aria-hidden="true">
    <div className="login-art__dashboard">
      <header><span /><i /><i /></header>
      <div className="login-art__welcome"><small>Good morning</small><strong>Your day at a glance</strong></div>
      <div className="login-art__tiles">
        <span><CalendarDays size={15} /><b>3</b><small>Today</small></span>
        <span><CheckCircle2 size={15} /><b>8</b><small>Tasks</small></span>
        <span><Users size={15} /><b>12</b><small>Team</small></span>
      </div>
    </div>
    <span className="login-art__float login-art__float--clock"><Clock3 size={17} /></span>
    <span className="login-art__float login-art__float--check"><CheckCircle2 size={17} /></span>
  </div>;
}

export function LoginPage() {
  const { bootstrap, loading, error: bootstrapError, login } = useApp();
  const [workspaces, setWorkspaces] = useState<Array<{ slug: string; name: string }>>([]);
  const [workspaceChoice, setWorkspaceChoice] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderPointerStart = useRef<number | null>(null);
  const appMode = isMobileAppRuntime();
  const mobilePresentation = appMode || (typeof window !== 'undefined' && window.matchMedia('(max-width: 650px)').matches);

  useEffect(() => {
    if (!mobilePresentation || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = window.setTimeout(() => setActiveSlide((current) => (current + 1) % mobileLoginSlides.length), 4800);
    return () => window.clearTimeout(timer);
  }, [activeSlide, mobilePresentation]);

  if (loading) return <div className="auth-loading full-page-state--brand"><BrandedLoading /></div>;
  if (bootstrap) return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError('');
    try { await login(email, password, workspaceChoice || undefined); }
    catch (requestError) {
      const problem = requestError as ApiError;
      if (problem.status === 409 && problem.details?.code === 'WORKSPACE_SELECTION_REQUIRED' && Array.isArray(problem.details.workspaces)) {
        setWorkspaces(problem.details.workspaces.filter((item): item is { slug: string; name: string } => Boolean(item && typeof item.slug === 'string' && typeof item.name === 'string')));
        setWorkspaceChoice('');
        setError('');
      } else {
        setError(problem.message);
      }
    }
    finally { setBusy(false); }
  }

  function finishSliderGesture(clientX: number) {
    if (sliderPointerStart.current === null) return;
    const distance = clientX - sliderPointerStart.current;
    sliderPointerStart.current = null;
    if (Math.abs(distance) < 38) return;
    setActiveSlide((current) => distance < 0
      ? (current + 1) % mobileLoginSlides.length
      : (current - 1 + mobileLoginSlides.length) % mobileLoginSlides.length);
  }

  return <main className={`login-page${mobilePresentation ? ' login-page--mobile' : ''}`}>
    <section className="login-story">
      <div className="login-story__brand"><BrandLogo surface="dark" /></div>
      <div className="login-story__content"><span className="login-kicker"><Sparkles size={15} /> One calm place to run the company</span><h1>Less searching.<br />More getting things done.</h1><p>Projects, people, attendance and finance—organized around what needs your attention today.</p><div className="login-benefits"><div><span><Gauge size={20} /></span><div><strong>Fast by default</strong><small>Focused screens and instant navigation.</small></div></div><div><span><ShieldCheck size={20} /></span><div><strong>Built around permissions</strong><small>Every role sees only what matters.</small></div></div><div><span><CheckCircle2 size={20} /></span><div><strong>Work stays connected</strong><small>One reliable record from task to payment.</small></div></div></div></div>
      <div className="login-story__footer"><span>Nosivo</span><span>Company operations workspace</span></div>
    </section>
    <section className="login-form-panel">
      {mobilePresentation ? <section className="login-mobile-slider" aria-label="Nosivo highlights" aria-roledescription="carousel"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') setActiveSlide((current) => (current + 1) % mobileLoginSlides.length);
          if (event.key === 'ArrowLeft') setActiveSlide((current) => (current - 1 + mobileLoginSlides.length) % mobileLoginSlides.length);
        }}
        onPointerDown={(event) => { sliderPointerStart.current = event.clientX; }}
        onPointerUp={(event) => finishSliderGesture(event.clientX)}
        onPointerCancel={() => { sliderPointerStart.current = null; }}>
        <div className="login-mobile-slider__brand"><BrandLogo surface="dark" /></div>
        <div className="login-mobile-slider__viewport">
          <div className="login-mobile-slider__track" style={{ transform: `translate3d(-${activeSlide * 100}%,0,0)` }}>
            {mobileLoginSlides.map((slide, index) => <article key={slide.id} className="login-mobile-slide" aria-hidden={index !== activeSlide}>
              <MobileLoginArtwork slide={slide.id} />
              <div className="login-mobile-slide__copy"><span>{slide.eyebrow}</span><h1>{slide.title}</h1><p>{slide.description}</p></div>
            </article>)}
          </div>
        </div>
        <div className="login-mobile-slider__dots" aria-label={`Slide ${activeSlide + 1} of ${mobileLoginSlides.length}`}>
          {mobileLoginSlides.map((slide, index) => <button key={slide.id} type="button" className={index === activeSlide ? 'is-active' : ''}
            onClick={() => setActiveSlide(index)} aria-label={`Show ${slide.eyebrow}`} aria-current={index === activeSlide ? 'true' : undefined} />)}
        </div>
      </section> : <div className="login-mobile-brand"><BrandLogo surface="light" /></div>}
      <div className="login-sheet">
        <div className="login-card"><header><span className="eyebrow">Secure sign-in</span><h2>Welcome back</h2><p>Use your email and password. We’ll find your workspace automatically.</p></header>
          {(error || bootstrapError) && <ErrorState message={error || bootstrapError || ''} />}
          <form onSubmit={submit} className="login-form login-form--matte"><label><span>Work email</span><input name="email" type="email" autoComplete="username" value={email} onChange={(event) => { setEmail(event.target.value); setWorkspaces([]); setWorkspaceChoice(''); setError(''); }} required /></label><label><span>Password</span><div className="password-input"><input name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => { setPassword(event.target.value); setWorkspaces([]); setWorkspaceChoice(''); setError(''); }} required /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div></label>{workspaces.length > 0 && <label><span>Choose company</span><select className="login-workspace-choice" required value={workspaceChoice} onChange={(event) => setWorkspaceChoice(event.target.value)}><option value="">Select a company to continue</option>{workspaces.map((item) => <option value={item.slug} key={item.slug}>{item.name}</option>)}</select><small className="login-workspace-hint">Credentials verified. Choose where you want to continue.</small></label>}<Button type="submit" busy={busy}>Sign in <ArrowRight size={16} /></Button></form>
        </div>
        <p className="login-help">Protected company system · Contact your administrator for access.<br /><a href="/privacy-policy">Privacy Policy</a><span aria-hidden="true"> · </span><a href="/support">Support</a></p>
      </div>
    </section>
  </main>;
}
